import { cn } from "../../lib";
import {
	CIRCLE_BUTTON_HOVER_WASH_CLASS,
	CIRCLE_BUTTON_SOLID_SYSTEM_SURFACE_CLASS,
} from "../custom/circle-button";

/**
 * Matches apps `SECONDARY_CTA_ICON_CIRCLE_SURFACE_CLASS` / {@link CircleButton}
 * solid system (platform-action / topic-viewer circle chrome). Kept local so
 * `@repo/ui` media-player does not depend on the web app CTA module.
 */
const MEDIA_PLAYER_SECONDARY_CTA_CIRCLE_SURFACE = cn(
	"relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
	"[&>*]:relative [&>*]:z-[1]",
	CIRCLE_BUTTON_SOLID_SYSTEM_SURFACE_CLASS,
	"transition-[opacity,background-color,color,box-shadow] duration-200",
	CIRCLE_BUTTON_HOVER_WASH_CLASS,
	"[&_svg]:!size-4 [&_svg]:block [&_svg]:shrink-0",
	"[&_svg]:[transform:translate3d(0,0,0)] [&_svg]:[backface-visibility:hidden]",
);

export interface MediaPlayerTheme {
	playButtonClassName: string;
	trackClassName: string;
	indicatorClassName: string;
	/** Continuation timer fill behind the playhead on card/audio scrub tracks. */
	seekBarContinuationClassName: string;
	seekBarTrackClassName: string;
	seekBarWaveformActiveClassName: string;
	seekBarWaveformGlowClassName: string;
	seekBarLockedZoneClassName: string;
	timestampClassName: string;
	skipButtonClassName: string;
	speedChipActiveClassName: string;
	speedChipInactiveClassName: string;
	ambientGlowColor: string;
	ambientParticleColor: string;
	/** Soft aurora behind scrub tracks (biome-aligned in learn surfaces). */
	trackGlowColor1: string;
	trackGlowColor2: string;
}

/** Light scrub rail — same in light and dark (text autoplay + audio). */
export const TOPIC_LEARN_SCRUB_TRACK_CLASSNAME = "!rounded-full !bg-white/30";

export const TOPIC_LEARN_SCRUB_INDICATOR_CLASSNAME = "!bg-white";

export const TOPIC_LEARN_SCRUB_CONTINUATION_CLASSNAME = "!bg-white/40";

export const TOPIC_LEARN_SCRUB_LOCKED_ZONE_CLASSNAME =
	"bg-black/10 dark:bg-white/5";

/** Narrow topic cards — square edge-to-edge translucent scrub (matches design ref). */
export const TOPIC_LEARN_SCRUB_TRACK_NARROW_CLASSNAME = cn(
	"!rounded-none !bg-white/30",
	"dark:!rounded-none dark:!bg-white/30",
);

/** Playhead fill stays white in narrow layout. */
export const TOPIC_LEARN_SCRUB_INDICATOR_NARROW_CLASSNAME =
	TOPIC_LEARN_SCRUB_INDICATOR_CLASSNAME;

export const TOPIC_LEARN_SCRUB_CONTINUATION_NARROW_CLASSNAME = cn(
	"!bg-white/40",
	"dark:!bg-white/40",
);

export const TOPIC_AUDIO_NARROW_TIMESTAMP_CLASSNAME =
	"min-w-[3.25rem] shrink-0 tabular-nums text-xs font-black text-slate-900 dark:text-white/80";

function applyNarrowScrubTheme(theme: MediaPlayerTheme): MediaPlayerTheme {
	return {
		...theme,
		trackClassName: TOPIC_LEARN_SCRUB_TRACK_NARROW_CLASSNAME,
		indicatorClassName: TOPIC_LEARN_SCRUB_INDICATOR_NARROW_CLASSNAME,
		seekBarContinuationClassName:
			TOPIC_LEARN_SCRUB_CONTINUATION_NARROW_CLASSNAME,
		seekBarTrackClassName: "bg-white/30 dark:bg-white/30",
	};
}

export const TOPIC_CARD_PLAYER_THEME: MediaPlayerTheme = {
	playButtonClassName: cn(
		MEDIA_PLAYER_SECONDARY_CTA_CIRCLE_SURFACE,
		"size-8 shrink-0",
	),
	trackClassName: TOPIC_LEARN_SCRUB_TRACK_CLASSNAME,
	indicatorClassName: TOPIC_LEARN_SCRUB_INDICATOR_CLASSNAME,
	seekBarContinuationClassName: TOPIC_LEARN_SCRUB_CONTINUATION_CLASSNAME,
	seekBarTrackClassName: "bg-white/30",
	seekBarWaveformActiveClassName: "",
	seekBarWaveformGlowClassName: "",
	seekBarLockedZoneClassName: TOPIC_LEARN_SCRUB_LOCKED_ZONE_CLASSNAME,
	timestampClassName: "",
	skipButtonClassName: "",
	speedChipActiveClassName: "",
	speedChipInactiveClassName: "",
	ambientGlowColor: "#ffffff",
	ambientParticleColor: "#ffffff",
	trackGlowColor1: "#ddd6fe",
	trackGlowColor2: "#b794f6",
};

export const TOPIC_AUDIO_PLAYER_THEME: MediaPlayerTheme = {
	playButtonClassName: cn(
		MEDIA_PLAYER_SECONDARY_CTA_CIRCLE_SURFACE,
		"size-16 shrink-0 shadow-xl",
	),
	trackClassName: TOPIC_LEARN_SCRUB_TRACK_CLASSNAME,
	indicatorClassName: TOPIC_LEARN_SCRUB_INDICATOR_CLASSNAME,
	seekBarContinuationClassName: TOPIC_LEARN_SCRUB_CONTINUATION_CLASSNAME,
	seekBarTrackClassName: "bg-white/30",
	seekBarWaveformActiveClassName: "fill-white/90 dark:fill-slate-100",
	seekBarWaveformGlowClassName:
		"drop-shadow-[0_0_8px_rgba(255,255,255,0.65)]",
	seekBarLockedZoneClassName: TOPIC_LEARN_SCRUB_LOCKED_ZONE_CLASSNAME,
	timestampClassName:
		"min-w-[3.25rem] shrink-0 tabular-nums text-xs font-black text-white/90 dark:text-white/80",
	skipButtonClassName:
		"size-9 shrink-0 rounded-full bg-white/10 text-white/90 backdrop-blur transition-colors hover:bg-white/20 dark:bg-zinc-900/40 dark:hover:bg-zinc-800/60",
	speedChipActiveClassName:
		"bg-white text-slate-900 shadow-sm dark:bg-white dark:text-slate-900",
	speedChipInactiveClassName:
		"bg-white/10 text-white/80 hover:bg-white/20 dark:bg-zinc-900/40 dark:text-white/80",
	ambientGlowColor: "#a78bfa",
	ambientParticleColor: "#c4b5fd",
	trackGlowColor1: "#ddd6fe",
	trackGlowColor2: "#b794f6",
};

export function resolveCardPlayerTheme(isNarrow: boolean): MediaPlayerTheme {
	if (!isNarrow) {
		return TOPIC_CARD_PLAYER_THEME;
	}
	return applyNarrowScrubTheme(TOPIC_CARD_PLAYER_THEME);
}

export function resolveAudioPlayerTheme(
	isNarrow: boolean,
	overrides: Partial<MediaPlayerTheme> = {},
): MediaPlayerTheme {
	const base: MediaPlayerTheme = {
		...TOPIC_AUDIO_PLAYER_THEME,
		...overrides,
	};
	if (!isNarrow) {
		return base;
	}
	return {
		...applyNarrowScrubTheme(base),
		timestampClassName: TOPIC_AUDIO_NARROW_TIMESTAMP_CLASSNAME,
	};
}
